import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import {HubspotForm} from '@/components/hubspot-form'
import Image from '@/components/image'
import LocalizedLink from '@/components/localized-link'
import PrismicLink from '@/components/prismic-link'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import {Text} from '@/components/text'
import useNavigate from '@/hooks/use-navigate'
import {useLocale} from '@/providers/locale'
import SignupBannerSection from '@/sections/signup-banner'
import {Section, Layout, container, BackgroundWrapper} from '@/styles/grid'
import {CheckmarkList} from '@/styles/lists'
import {tokens, colors} from '@/theme'
import {LifecycleStages, LatestLeadSourceCaptureType} from '@/types/marketing-campaigns'
import getEnvironmentField from '@/utils/get-environment-field'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import type {State as HubspotFormState} from '@/components/hubspot-form'
import type {EbookPageQuery} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import type {PageProps} from 'gatsby'

import {ebookFormSubmitted, bigformInteracted, bigformReached} from './tracking'

type Props = PageProps<EbookPageQuery>

const Ebook = (props: Props) => {
    const ebookData = props.data.prismicEbook.data
    const pageData = ebookData.predefined_details.document.data
    const [screen, setScreen] = React.useState<'form' | 'download'>('form')

    const currentLocale = useLocale()

    const navigate = useNavigate()

    // If the ebook has a file specified, we display the Download button after
    // the form is successfully submitted. Otherwise, we redirect to a generic
    // "Thank You" page and the download link is sent to the email.
    const onFormSubmit = (state: HubspotFormState) => {
        ebookFormSubmitted(state)

        if (!ebookData.file?.url) {
            navigate('/thankyou-ebook')
            return
        }

        setScreen('download')
    }

    if (!ebookData || !pageData) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <Main>
                {screen === 'form' && (
                    <HeroBackground>
                        <HeroSection as={Section} $variant="two-asymmetrical">
                            <Image image={ebookData.cover_image} width={800} />

                            <HubspotForm
                                redirectCallback={onFormSubmit}
                                formConfig={
                                    getEnvironmentField({
                                        production: ebookData.form_production,
                                        staging: ebookData.form_staging,
                                    }) as FormConfig
                                }
                                imperativeConsents={getEnvironmentField({
                                    production: ebookData.imperative_consents_production,
                                    staging: ebookData.imperative_consents_staging,
                                })}
                                hubspotProperties={{
                                    lifecyclestage: LifecycleStages.Subscriber,
                                    campaign__updating_: ebookData.campaign,
                                    lead_source_latest_capture_type:
                                        LatestLeadSourceCaptureType.Ebooks,
                                }}
                                content={{
                                    preTitle: ebookData.form_pre_title ?? pageData.form_pre_title,
                                    title: ebookData.title,
                                }}
                                onFormReached={() =>
                                    bigformReached({
                                        country: currentLocale.country,
                                    })
                                }
                                onFormInteracted={(key) => bigformInteracted(key)}
                            />
                        </HeroSection>
                    </HeroBackground>
                )}

                {screen === 'download' && ebookData.file?.url && (
                    <BackgroundWrapper $color="yellow">
                        <Section>
                            <FeatureBlock
                                image={pageData.thank_you_image}
                                title={pageData.thank_you_heading.replace(
                                    /({{.*}})/,
                                    ebookData.title,
                                )}
                                body={pageData.thank_you_text.richText}
                            >
                                <ActionsRow>
                                    <Button as={PrismicLink} {...ebookData.file}>
                                        {pageData.thank_you_download_button_label}
                                    </Button>
                                    <Button as={LocalizedLink} to="/ebooks" $variant="outline">
                                        {pageData.thank_you_return_button_label}
                                    </Button>
                                </ActionsRow>
                            </FeatureBlock>
                        </Section>
                    </BackgroundWrapper>
                )}

                <Layout as={Section} $variant="two-asymmetrical" $alignItems="center">
                    <WhatsInsideTextWrapper>
                        <Text as="h2" $variant="heading-base">
                            {pageData.what_s_inside_text}
                        </Text>

                        <WhatsInsidePoints>
                            {ebookData.what_s_inside_points.map(({point}, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </WhatsInsidePoints>
                    </WhatsInsideTextWrapper>

                    <Image image={ebookData.what_s_inside_image} width={600} />
                </Layout>

                {pageData.signup_banner && (
                    <SignupBannerSection
                        {...pageData.signup_banner.document.data}
                        color="pink"
                        formConfig={
                            getEnvironmentField({
                                production: pageData.signup_banner.document.data.form_production,
                                staging: pageData.signup_banner.document.data.form_staging,
                            }) as FormConfig
                        }
                        placeholder={pageData.signup_banner.document.data.email_input_placeholder}
                        submitText={pageData.signup_banner.document.data.submit_text}
                        errorText={pageData.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<EbookPageQuery>) => {
    const pageData = data.prismicEbook.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
        />
    )
}

const mobileBreakpoint = 'xs'

const HeroBackground = styled.div`
    ${mq.min(mobileBreakpoint)} {
        background: ${colors.background.muted};
    }
`

const HeroSection = styled(Layout)`
    display: grid;
    padding-right: 0;
    padding-left: 0;
    align-items: center;
    justify-items: center;

    ${mq.min(mobileBreakpoint)} {
        ${container}
    }

    ${mq.max('md')} {
        grid-template-columns: 1fr;
    }

    > &:first-child {
        ${mq.max('md')} {
            grid-row: 2;
        }
    }
`

const WhatsInsideTextWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};

    ${mq.min('sm')} {
        row-gap: ${tokens.spacing20};
    }
`

const ActionsRow = styled.div`
    display: flex;

    > * + * {
        margin-left: 10px;
    }
`

const WhatsInsidePoints = styled(CheckmarkList)`
    li {
        color: ${colors.text.muted};
    }
`

export const query = graphql`
    query EbookPage($uid: String!, $locale: String!) {
        prismicEbook(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                exclude_robots
                form_production {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
                form_staging {
                    formId
                    consents {
                        type
                        communicationConsents {
                            id
                            required
                        }
                    }
                }
                imperative_consents_production {
                    consent {
                        document {
                            ...ConsentWebsite
                        }
                    }
                }
                imperative_consents_staging {
                    consent {
                        document {
                            ...ConsentWebsite
                        }
                    }
                }
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                predefined_details {
                    document {
                        ... on PrismicEbookDetails {
                            data {
                                form_pre_title
                                form_submit_text
                                what_s_inside_text
                                signup_banner {
                                    document {
                                        ...SignupBanner
                                    }
                                }
                                thank_you_image {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                                thank_you_text {
                                    richText
                                }
                                thank_you_heading
                                thank_you_return_button_label
                                thank_you_download_button_label
                            }
                        }
                    }
                }
                form_pre_title
                title
                campaign
                cover_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                what_s_inside_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                what_s_inside_points {
                    point
                }
                file {
                    ...PrismicLink
                }
            }
        }
    }
`

export default withPrismicPreview(Ebook)
